/**
 * Centralized error logging utility
 * This can be extended to send errors to external services like Sentry, LogRocket, etc.
 */

class ErrorLogger {
  constructor() {
    this.errors = [];
    this.maxErrors = 100; // Keep only last 100 errors in memory
  }

  /**
   * Log an error with additional context
   * @param {Error} error - The error object
   * @param {Object} errorInfo - Additional error information
   * @param {string} componentName - Name of the component where error occurred
   * @param {Object} additionalContext - Any additional context
   */
  logError(error, errorInfo = null, componentName = 'Unknown', additionalContext = {}) {
    const errorLog = {
      timestamp: new Date().toISOString(),
      error: error.toString(),
      stack: error.stack,
      componentName,
      errorInfo,
      additionalContext,
      userAgent: navigator.userAgent,
      url: window.location.href,
      errorId: this.generateErrorId()
    };

    // Add to local storage for debugging
    this.addToLocalStorage(errorLog);

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group(`🚨 Error in ${componentName}`);
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Additional Context:', additionalContext);
      console.error('Error Log:', errorLog);
      console.groupEnd();
    }

    // Add to in-memory array
    this.errors.push(errorLog);
    if (this.errors.length > this.maxErrors) {
      this.errors.shift(); // Remove oldest error
    }

    // TODO: Send to external error reporting service
    // this.sendToErrorService(errorLog);
  }

  /**
   * Generate a unique error ID
   * @returns {string} Unique error ID
   */
  generateErrorId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /**
   * Add error to local storage for debugging
   * @param {Object} errorLog - Error log object
   */
  addToLocalStorage(errorLog) {
    try {
      const existingErrors = JSON.parse(localStorage.getItem('qmentisai_errors') || '[]');
      existingErrors.push(errorLog);
      
      // Keep only last 50 errors in localStorage
      if (existingErrors.length > 50) {
        existingErrors.splice(0, existingErrors.length - 50);
      }
      
      localStorage.setItem('qmentisai_errors', JSON.stringify(existingErrors));
    } catch (e) {
      console.warn('Failed to save error to localStorage:', e);
    }
  }

  /**
   * Get all logged errors
   * @returns {Array} Array of error logs
   */
  getErrors() {
    return [...this.errors];
  }

  /**
   * Clear all logged errors
   */
  clearErrors() {
    this.errors = [];
    try {
      localStorage.removeItem('qmentisai_errors');
    } catch (e) {
      console.warn('Failed to clear errors from localStorage:', e);
    }
  }

  /**
   * Get errors from localStorage
   * @returns {Array} Array of error logs from localStorage
   */
  getErrorsFromStorage() {
    try {
      return JSON.parse(localStorage.getItem('qmentisai_errors') || '[]');
    } catch (e) {
      console.warn('Failed to get errors from localStorage:', e);
      return [];
    }
  }

  /**
   * Send error to external service (placeholder for future implementation)
   * @param {Object} errorLog - Error log object
   */
  sendToErrorService(errorLog) {
    // TODO: Implement sending to external service like Sentry, LogRocket, etc.
    // Example:
    // if (window.Sentry) {
    //   window.Sentry.captureException(errorLog.error, {
    //     extra: errorLog
    //   });
    // }
  }

  /**
   * Report error to analytics or monitoring service
   * @param {string} errorType - Type of error
   * @param {Object} metadata - Additional metadata
   */
  reportError(errorType, metadata = {}) {
    // TODO: Implement analytics reporting
    // Example:
    // if (window.gtag) {
    //   window.gtag('event', 'error', {
    //     event_category: 'error_boundary',
    //     event_label: errorType,
    //     value: 1,
    //     custom_parameters: metadata
    //   });
    // }
  }
}

// Create singleton instance
const errorLogger = new ErrorLogger();

export default errorLogger; 